import { Combobox, Option, Skeleton, SkeletonItem, mergeClasses } from "@fluentui/react-components";
import { GetScopeValuesRequest, ScopeItem } from "Shared/Components/Scope/Scopes.types";
import { OptionOnSelectData, SelectionEvents } from "Shared/FluentUIUnstableDef";
import React, { useCallback, useMemo } from "react";
import { observer, useLocalObservable } from "mobx-react";

import { M365Alert } from "../Alert/M365Alert";
import { M365PulseInfiniteScroll } from "Shared/Components";
import { RenderingStatus } from "../../../Enumerations/RenderingStatus";
import { ScopePivotValueDropdownProps } from "./ScopePivotValueDropdown.types";
import { useCloseOnScroll } from "Shared/Hooks/Util/useCloseOnScroll";
import { useCompositeTopologyService } from "./Topology/useCompositeTopologyService";
import { useQuery } from "@tanstack/react-query";
import { useScopePivotValueDropdownStyles } from "./ScopePivotValueDropdown.styles";

const PAGE_SIZE = 10;

const ScopePivotValueDropdownBase = (props: ScopePivotValueDropdownProps) => {
    const topologyService = useCompositeTopologyService();
    const classNames = useScopePivotValueDropdownStyles();
    const mergedRootClassName = mergeClasses(classNames.root, props.className);
    const localStore = useLocalObservable(() => ({
        open: false,
        searchText: "",
        sliceIndexLimit: PAGE_SIZE
    }));

    const requiredScopes = useMemo((): ScopeItem[] => {
        const selectedScopesSoFar = (props.selectedScopes || []).filter((scope) => scope.ScopeType?.toLowerCase() !== "global");
        const currentScopePosition = selectedScopesSoFar.findIndex((scope) => scope.ScopeType === props.scopeTarget.ScopeType);
        return selectedScopesSoFar.slice(0, currentScopePosition);
    }, [props.scopeTarget.ScopeType, props.selectedScopes]);

    const scopeRequest = useMemo((): GetScopeValuesRequest => {
        const { scopeHierarchy, scopeTarget } = props;
        return {
            scopeHierarchyId: scopeHierarchy.HierarchyId,
            currentScopeTarget: scopeTarget,
            selectedScopesSoFar: requiredScopes,
            context: props.context
        };
    }, [props, requiredScopes]);

    const queryKey = useMemo(() => {
        const keys = [scopeRequest.scopeHierarchyId, scopeRequest.currentScopeTarget.ScopeType, JSON.stringify(scopeRequest.context || "")];
        const selectedScopeKeys = requiredScopes.map((scope) => `${scope.ScopeType}-${scope.Value}`).join("-");
        keys.push(selectedScopeKeys);
        return keys.join(",");
    }, [requiredScopes, scopeRequest.context, scopeRequest.currentScopeTarget.ScopeType, scopeRequest.scopeHierarchyId]);

    const hasAllRequiredParentScopeValues = useMemo(() => {
        const hasVal = requiredScopes.every((scope) => !!scope.Value);
        return hasVal;
    }, [requiredScopes]);

    const { data: scopeValues, status: loadingStatus } = useQuery({
        queryKey: [queryKey],
        queryFn: () => (hasAllRequiredParentScopeValues ? topologyService.getScopeValueOptions(scopeRequest) : []),

        staleTime: Infinity,
        enabled: localStore.open
    });

    const availableOptions = useMemo(() => {
        if (loadingStatus === RenderingStatus.Ready) {
            return scopeValues.sort((a, b) => (a.Value || "").localeCompare(b.Value || ""));
        } else if (props.defaultSelection) {
            return [props.defaultSelection];
        }
        return [];
    }, [loadingStatus, props.defaultSelection, scopeValues]);

    const comboboxRef = useCloseOnScroll({
        isOpen: localStore.open,
        onClose: () => {
            localStore.open = false;
        }
    });

    const onOpenChange = useCallback(
        (_e: React.SyntheticEvent, data: { open: boolean }) => {
            localStore.open = data.open;
            if (!localStore.open) {
                localStore.sliceIndexLimit = PAGE_SIZE;
                localStore.searchText = "";
            }
        },
        [localStore]
    );

    const onChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            localStore.searchText = event.target.value.trim();
        },
        [localStore]
    );

    const onOptionSelect = useCallback(
        (_event: SelectionEvents, data: OptionOnSelectData) => {
            const selected = availableOptions.find((option) => option.Value === data.optionValue);
            if (selected) {
                props.onSelect?.(selected);
            }
        },
        [availableOptions, props]
    );

    const loadingSection = useMemo(() => {
        if (loadingStatus === RenderingStatus.Waiting) {
            return (
                <Skeleton>
                    <SkeletonItem className={classNames.skeletonItem} shape="rectangle" size={16} />
                    <SkeletonItem className={classNames.skeletonItem} shape="rectangle" size={16} />
                </Skeleton>
            );
        } else if (loadingStatus === RenderingStatus.Error) {
            return <M365Alert intent="error" message="Something went wrong" />;
        } else if (!hasAllRequiredParentScopeValues) {
            return <M365Alert intent="info" message="Please select all parent scope values" />;
        } else if (!availableOptions.length) {
            return <M365Alert intent="info" message="No data" />;
        }
        return null;
    }, [availableOptions.length, classNames.skeletonItem, hasAllRequiredParentScopeValues, loadingStatus]);

    const comboBoxListSlot = useMemo(() => ({ className: classNames.comboBoxListBox }), [classNames.comboBoxListBox]);

    const continueLoad = useCallback(() => {
        const values = scopeValues || [];
        if (values.length > localStore.sliceIndexLimit + 1) {
            localStore.sliceIndexLimit += PAGE_SIZE;
        }
    }, [localStore, scopeValues]);

    const visibleOptions = useMemo(
        () =>
            localStore.searchText
                ? availableOptions.filter((scopeValue) => scopeValue.Value?.toLowerCase().includes(localStore.searchText.toLowerCase()))
                : availableOptions.slice(0, localStore.sliceIndexLimit),
        [availableOptions, localStore.searchText, localStore.sliceIndexLimit]
    );

    return (
        <div className={mergedRootClassName} ref={comboboxRef}>
            <Combobox
                key={getComboxId(props)}
                onChange={onChange}
                listbox={comboBoxListSlot}
                defaultValue={getScopeTextValue(props.defaultSelection)}
                open={localStore.open}
                onOpenChange={onOpenChange}
                onOptionSelect={onOptionSelect}
                autoComplete="off"
                aria-autocomplete="none"
                aria-label={props.scopeTarget.DisplayName}
            >
                {loadingStatus === RenderingStatus.Ready && (
                    <M365PulseInfiniteScroll onBottomVisible={continueLoad} loadingStatus={RenderingStatus.Ready}>
                        {visibleOptions.map((scopeOption) => (
                            <Option key={`${scopeOption.ScopeType}${scopeOption.Value}`} text={getScopeTextValue(scopeOption)} value={scopeOption.Value || ""}>
                                {getScopeTextValue(scopeOption)}
                            </Option>
                        ))}
                    </M365PulseInfiniteScroll>
                )}
                {loadingSection}
            </Combobox>
        </div>
    );
};

export const ScopePivotValueDropdown = observer(ScopePivotValueDropdownBase);

export const getScopeTextValue = (scopeItem?: ScopeItem) => {
    if (scopeItem?.Value === "*") {
        return "ALL";
    }
    return (scopeItem?.ExtraProperties?.Alias || scopeItem?.Value || "").toUpperCase();
};

const getComboxId = (props: ScopePivotValueDropdownProps) => {
    let id = `${props.scopeHierarchy.HierarchyId}-${props.scopeTarget.ScopeType}`;
    for (const scope of props.selectedScopes || []) {
        if (scope.ScopeType === props.scopeTarget.ScopeType) {
            return id;
        }
        id += `-${scope.Value}`;
    }

    return id;
};
